import { useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getSearchHistory, getSearchById } from '../services/searchHistoryService';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import config from '../config';
import searchHistory from '../components/home/sections/SearchHistory';
import { useAuth } from '../context/AuthContext';

const STORAGE_KEY = 'previousSearches';

const useSearchHistory = () => {
  const queryClient = useQueryClient();
  const currentUser = useAuth().currentUser ?? { id: 'guest' };
  const token = localStorage.getItem('token');

  const {
    data: serverHistory = [],
    refetch,
    error,
    isLoading,
  } = useQuery({
    queryKey: ['searchHistory', currentUser?.id],
    queryFn: async () => {
      if (!token) {
        return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      }
      const response = await axios({
        method: 'get',
        url: `${config.apiUrl}/search_history/get_search_history`, // adapt to your real endpoint
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const readLocal = () => JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  const writeLocal = (searches) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(searches));
  };

  // 3) CREATE or UPDATE a search
  const addOrUpdateSearchMutation = useMutation({
    mutationKey: ['addOrUpdateSearchMutation', currentUser?.id],
    mutationFn: async function (searchData) {
      if (!token) {
        return { localOnly: true, data: searchData };
      }

      const isUpdate = searchData.id && !String(searchData.id).startsWith('temp-');

      const endpoint = isUpdate
        ? `/search_history/update_search/${searchData.id}`
        : '/search_history/save_search';

      const method = isUpdate ? 'POST' : 'POST';
      // or 'PUT' for updates, depends on your backend

      const result = await axios({
        method: method,
        url: `${config.apiUrl}${endpoint}`,
        data: searchData,
        headers: { Authorization: `Bearer ${token}` },
      });

      return { localOnly: false, data: result };
    },
    onSuccess: (res, variables) => {
      if (res.localOnly) {
        // no token => update local searches only
        const local = readLocal();
        const isUpdate = variables.id && !String(variables.id).startsWith('temp-');
        let updated = [];

        if (isUpdate) {
          // replace existing
          updated = local.map((item) =>
            item.id === variables.id ? { ...item, ...variables } : item,
          );
        } else {
          // create new
          updated = [{ ...variables, id: variables.id || `temp-${uuidv4()}` }, ...local];
        }
        writeLocal(updated);
      } else {
        // server writes => replace or refetch
        // simpler to just refetch for up-to-date data
        queryClient.invalidateQueries(['searchHistory', currentUser?.id]);
      }
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // 4) DELETE a search
  const deleteSearchMutation = useMutation({
    mutationKey: ['deleteSearchMutation', currentUser?.id],
    mutationFn: async (searchId) => {
      if (!token) {
        // local only
        return { localOnly: true, id: searchId };
      }
      // real server call
      await axios({
        method: 'delete',
        url: `${config.apiUrl}/search_history/searches/${searchId}`,
        headers: { Authorization: `Bearer ${token}` },
      });
      return { localOnly: false, id: searchId };
    },
    onSuccess: (res) => {
      if (res.localOnly) {
        // remove from local
        const local = readLocal();
        const updated = local.filter((item) => item.id !== res.id);
        writeLocal(updated);
      } else {
        queryClient.invalidateQueries(['searchHistory', currentUser?.id]);
      }
    },
  });

  // 5) Provide easy callbacks for the UI
  const addOrUpdateSearch = useCallback(
    (searchData) => {
      // property_type default
      if (!searchData.property_type) {
        searchData.property_type = 'buy';
      }
      // set timestamp
      if (!searchData.timestamp) {
        searchData.timestamp = new Date().toISOString();
      }
      addOrUpdateSearchMutation.mutate(searchData);
    },
    [addOrUpdateSearchMutation],
  );

  const deleteSearch = useCallback(
    (searchId) => {
      deleteSearchMutation.mutate(searchId);
    },
    [deleteSearchMutation],
  );

  const getRecentSearches = useCallback(
    (count = 5) => {
      return serverHistory.slice(0, count);
    },
    [serverHistory],
  );

  return {
    searchHistory: Array.isArray(serverHistory) ? serverHistory : [],
    isLoading: isLoading || addOrUpdateSearchMutation.isLoading || deleteSearchMutation.isLoading,
    error: error || addOrUpdateSearchMutation.error || deleteSearchMutation.error,
    addSearch: addOrUpdateSearch,
    handleSaveSearch: addOrUpdateSearch,
    removeSearch: deleteSearch,
    handleDeleteSearch: deleteSearch,
    getRecentSearches,
    getSearchById,
  };
};

export default useSearchHistory;
